import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { 
  calculateTalentRating, 
  TalentRatingInput, 
  TalentTier,
  getTierForScore 
} from '@/utils/talentRating';

export interface BrandAthlete {
  id: string;
  profileId: string;
  name: string;
  sport: string;
  avgEngagementRate: number;
  campaignsCount: number;
  followersCount: number;
  totalReach: number;
  paymentsDue: number;
  lastCampaignDate: string;
  athleteScore: number;
  tier: TalentTier;
  ratingBreakdown?: {
    athletic: number;
    social: number;
    business: number;
    profile: number;
  };
  grade?: string;
  avatarUrl?: string;
}

const generateMockAthletes = (): BrandAthlete[] => {
  // Generate realistic mock data using the rating system
  const mockData = [
    { name: 'Sarah Johnson', sport: 'basketball', followers: 485000, engagement: 8.5, deals: 12, revenue: 125000 },
    { name: 'Marcus Williams', sport: 'football', followers: 320000, engagement: 7.2, deals: 8, revenue: 85000 },
    { name: 'Emma Rodriguez', sport: 'football', followers: 680000, engagement: 9.1, deals: 15, revenue: 175000 },
    { name: 'Jake Thompson', sport: 'baseball', followers: 275000, engagement: 6.8, deals: 10, revenue: 67000 },
    { name: 'Olivia Chen', sport: 'football', followers: 420000, engagement: 7.9, deals: 14, revenue: 95000 },
    { name: 'Carlos Mendez', sport: 'basketball', followers: 180000, engagement: 5.4, deals: 6, revenue: 45000 },
    { name: 'Sophia Davis', sport: 'formula-1', followers: 510000, engagement: 8.3, deals: 11, revenue: 145000 },
    { name: 'Ryan O\'Connor', sport: 'football', followers: 125000, engagement: 4.9, deals: 5, revenue: 28000 },
    { name: 'Zoe Kim', sport: 'football', followers: 195000, engagement: 6.1, deals: 7, revenue: 52000 },
    { name: 'Tyler Brooks', sport: 'nfl', followers: 89000, engagement: 3.8, deals: 3, revenue: 18000 },
  ];

  return mockData.map((data, index) => {
    const ratingInput: TalentRatingInput = {
      sport: data.sport,
      followers: data.followers,
      engagementRate: data.engagement,
      completedDeals: data.deals,
      totalRevenue: data.revenue,
      isVerified: data.followers > 300000,
      isClaimed: true,
      hasPhoto: true,
      platformCount: 3,
    };

    const rating = calculateTalentRating(ratingInput);

    return {
      id: `mock-${index + 1}`,
      profileId: `mock-${index + 1}`,
      name: data.name,
      sport: data.sport.charAt(0).toUpperCase() + data.sport.slice(1),
      avgEngagementRate: data.engagement,
      campaignsCount: data.deals,
      followersCount: data.followers,
      totalReach: data.followers,
      paymentsDue: data.revenue,
      lastCampaignDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      athleteScore: rating.overall,
      tier: rating.tier,
      ratingBreakdown: rating.breakdown,
      grade: rating.grade,
    };
  });
};

export const useBrandAthletes = () => {
  const [athletes, setAthletes] = useState<BrandAthlete[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchBrandAthletes = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      // Fetch deals for this brand with talent data
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select(`
          id,
          deal_value,
          status,
          talent_profile_id,
          created_at
        `)
        .eq('brand_profile_id', currentUser.id);

      if (dealsError) throw dealsError;

      if (!dealsData || dealsData.length === 0) {
        setAthletes(generateMockAthletes());
        return;
      }

      // Fetch talent and profile data separately - filter out null talent_profile_ids
      const talentProfileIds = [...new Set(
        dealsData
          .map(deal => deal.talent_profile_id)
          .filter(id => id != null)
      )];
      
      // If no assigned talents found, show empty state
      if (talentProfileIds.length === 0) {
        setAthletes([]);
        return;
      }

      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', talentProfileIds);

      const { data: talentsData } = await supabase
        .from('talents')  
        .select('id, profile_id, name, sport, total_followers, stats, achievements')
        .in('profile_id', talentProfileIds);

      // Process deals data to create athlete summaries
      const athleteMap = new Map<string, BrandAthlete>();
      
      dealsData.forEach(deal => {
        const talentProfile = profilesData?.find(p => p.id === deal.talent_profile_id);
        const talentDetails = talentsData?.find(a => a.profile_id === deal.talent_profile_id);
        
        if (talentProfile) {
          const athleteId = talentProfile.id;
          const paymentsDue = deal.status === 'active' || deal.status === 'pending' ? (deal.deal_value || 0) : 0;
          
          if (athleteMap.has(athleteId)) {
            const existing = athleteMap.get(athleteId)!;
            existing.campaignsCount += 1;
            existing.paymentsDue += paymentsDue;
            if (deal.created_at > existing.lastCampaignDate) {
              existing.lastCampaignDate = deal.created_at;
            }
          } else {
            const followers = (talentDetails as any)?.total_followers || 0;
            const stats = (talentDetails as any)?.stats as Record<string, any> || {};
            const engagementRate = stats.engagement_rate || 0;
            
            // Calculate comprehensive rating using the new system
            const ratingInput: TalentRatingInput = {
              sport: (talentDetails as any)?.sport || 'football',
              followers: followers,
              engagementRate: engagementRate,
              completedDeals: 0,
              totalRevenue: 0,
              isVerified: false,
              isClaimed: true,
              hasPhoto: !!talentProfile.avatar_url,
              platformCount: 2,
            };

            const rating = calculateTalentRating(ratingInput);

            athleteMap.set(athleteId, {
              id: athleteId,
              profileId: talentProfile.id,
              name: (talentDetails as any)?.name || talentProfile.display_name || 'Unknown Athlete',
              sport: (talentDetails as any)?.sport || 'Unknown',
              avgEngagementRate: engagementRate,
              campaignsCount: 1,
              followersCount: followers,
              totalReach: followers,
              paymentsDue: paymentsDue,
              lastCampaignDate: deal.created_at,
              athleteScore: rating.overall,
              tier: rating.tier,
              ratingBreakdown: rating.breakdown,
              grade: rating.grade,
              avatarUrl: talentProfile.avatar_url,
            });
          }
        }
      });

      const athletesArray = Array.from(athleteMap.values());
      setAthletes(athletesArray);
    } catch (err) {
      console.error('Error fetching brand athletes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch athletes');
      // Fallback to mock data
      setAthletes(generateMockAthletes());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandAthletes();
  }, [currentUser?.id]);

  const handleViewHistory = (athlete: BrandAthlete) => {
    // Navigate to deals page filtered by athlete or show modal
  };

  return {
    athletes,
    loading,
    error,
    fetchBrandAthletes,
    handleViewHistory
  };
};
